<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { type NavSection } from '../../navigation'
import { useEscape } from '../../composables/useEscape'
import { signOut } from '../../signOut'
import { isActive } from './useActiveNav'
import NavIcon from './NavIcon.vue'
import NavBadge from './NavBadge.vue'

withDefaults(defineProps<{ sections: NavSection[]; badges?: Record<string, string> }>(), { badges: () => ({}) })
const emit = defineEmits<{ close: [] }>()

useEscape(() => emit('close'))

const itemClass = (href: string) =>
  isActive(href)
    ? 'flex items-center gap-3 rounded-md bg-accent-soft px-2 py-3 text-[15px] font-semibold text-accent-ink'
    : 'flex items-center gap-3 rounded-md px-2 py-3 text-[15px] text-text hover:bg-surface-2'
</script>

<template>
  <div class="fixed inset-0 z-40 flex items-end bg-black/40 lg:hidden" @click.self="emit('close')">
    <!-- max-h-[85svh] + flex-col: над шторкой всегда остаётся полоса фона, по которой её
         можно закрыть тапом, сколько бы пунктов ни добавили. Скроллится только средний блок. -->
    <div class="flex max-h-[85svh] w-full flex-col rounded-t-2xl bg-surface shadow-2xl"
         role="dialog" aria-modal="true" aria-label="Меню">
      <div class="flex flex-none items-center px-3 pt-2">
        <!-- Распорка шириной с кнопку закрытия: держит «ручку» по центру шторки. -->
        <span class="h-11 w-11 flex-none" aria-hidden="true" />
        <div class="mx-auto h-1 w-10 rounded-full bg-border-strong" />
        <button type="button" data-testid="more-close" aria-label="Закрыть меню" @click="emit('close')"
                class="flex h-11 w-11 flex-none items-center justify-center rounded-md text-muted hover:bg-surface-2">
          <NavIcon name="close" class="h-5 w-5" />
        </button>
      </div>
      <div data-testid="more-scroll" class="flex-1 overflow-y-auto overscroll-contain px-3 pb-2">
        <template v-for="section in sections" :key="section.key">
          <template v-if="section.items.length">
            <!-- Со второй группы — линия и воздух сверху: иначе заголовок стоит к прошлому
                 пункту так же близко, как пункты друг к другу, и группы сливаются в один список.
                 first: — а не индекс секции: пустая первая секция не рисуется. -->
            <p class="mt-2 border-t border-border px-2 pb-1 pt-4 text-xs font-bold uppercase tracking-wide text-muted first:mt-0 first:border-t-0 first:pt-2">{{ section.title }}</p>
            <component :is="item.external ? 'a' : Link" v-for="item in section.items" :key="item.key" :href="item.href"
                  @click="emit('close')"
                  :data-testid="`more-item-${item.key}`" :class="itemClass(item.href)"
                  :aria-current="isActive(item.href) ? 'page' : undefined">
              <NavIcon :name="item.icon" class="h-5 w-5 text-muted" /> {{ item.label }}
              <NavBadge v-if="badges[item.key]" :label="badges[item.key]" class="ml-auto" />
            </component>
          </template>
        </template>
      </div>
      <!-- «Выйти» вне прокрутки: разрушительное действие не уезжает и не ловит случайный
           тап при скролле. Отступ снизу — не меньше прежнего pb-6 плюс safe-area. -->
      <div class="flex-none border-t border-border px-3 pt-2 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <button type="button" data-testid="more-signout" @click="signOut"
                class="flex w-full items-center gap-3 rounded-md px-2 py-3 text-[15px] text-crit hover:bg-surface-2">
          <NavIcon name="logout" class="h-5 w-5" /> Выйти
        </button>
      </div>
    </div>
  </div>
</template>
